import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Layout from "./components/Layout";
import AdminLayout, { CustomerPreviewBanner } from "./components/AdminLayout";
import BackToRouteFab from "./components/BackToRouteFab";
import Home from "./pages/Home";
import Book from "./pages/Book";
import Estimate from "./pages/Estimate";
import RefLandingPage from "./pages/RefLandingPage";
import Reviews from "./pages/Reviews";
import Gallery from "./pages/Gallery";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import PaymentMethods from "./pages/PaymentMethods";
import Admin from "./pages/Admin";
import CustomerBookings from "./pages/CustomerBookings";
import Checkout from "./pages/Checkout";
import PaymentSuccess from "./pages/PaymentSuccess";
import AuthCallback from "./pages/AuthCallback";
import MagicLogin from "./pages/MagicLogin";
import PayInvoiceByToken from "./pages/PayInvoiceByToken";
import Tip from "./pages/Tip";
import ClaimAccount from "./pages/ClaimAccount";
import ReferralWelcomeSplash from "./components/ReferralWelcomeSplash";
import Quote from "./pages/Quote";
import BookLanding from "./pages/BookLanding";
import InstantQuoteWizard from "./pages/InstantQuoteWizard";
import RequestQuote from "./pages/RequestQuote";
import Receipt from "./pages/Receipt";
import BookingConfirm from "./pages/BookingConfirm";
import Offer from "./pages/Offer";
// iter150 — Public policy pages (Twilio A2P 10DLC compliance). MUST
// render publicly at fixed URLs with no auth redirect.
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import "@/App.css";

/* Routes that ALWAYS render in plain (no chrome) layout — payment redirects,
   confirmation pages, etc. So admins coming back from Stripe don't get
   bounced to /admin. */
const RAW_ROUTE_PREFIXES = [
  "/auth/", "/checkout/", "/payment/", "/tip/", "/claim/",
  "/booking/", "/quote/", "/receipt/", "/offer/",
  // iter223 — Public, no-login-required invoice link (token-scoped).
  "/pay/",
];
const RAW_ROUTE_EXACT = new Set([
  "/login",
  // iter150 — Twilio A2P compliance: policy pages MUST render standalone
  // (no admin/customer chrome, no auth guard).
  "/privacy-policy", "/privacy",
  "/terms-and-conditions", "/terms",
]);
function isRawRoute(pathname) {
  if (RAW_ROUTE_EXACT.has(pathname)) return true;
  return RAW_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));
}

function isCustomerOverrideOn() {
  try { return sessionStorage.getItem("jdsn_customer_view_override") === "1"; }
  catch { return false; }
}

function AppRouter() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = !!(user?.is_admin || user?.role === "admin");
  const overrideOn = isAdmin && isCustomerOverrideOn();

  // Auto-redirect admin from customer landing page → admin overview, unless
  // they explicitly opened Customer Preview from Settings.
  useEffect(() => {
    if (loading) return;
    if (!isAdmin) return;
    if (overrideOn) return;
    if (location.pathname === "/") {
      navigate("/admin?tab=bookings", { replace: true });
    }
  }, [loading, isAdmin, overrideOn, location.pathname, navigate]);

  // iter233 — Landing page IS the new conversion-focused Home
  // (address → contact → service → SEE MY PRICE → book). No client
  // redirect from `/` anymore. The old Estimate flow lives at /estimate
  // and remains available for direct links.

  // Renders the routes table once — always the same.
  const routesTable = (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/home" element={<Home />} />
      <Route path="/book" element={<BookLanding />} />
      <Route path="/book/legacy" element={<Book />} />
      <Route path="/quote/instant/:service" element={<InstantQuoteWizard />} />
      <Route path="/quote/request" element={<RequestQuote />} />
      <Route path="/estimate" element={<Estimate />} />
      {/* iter133 — Public referral landing (attributes referrer + redirects to estimate) */}
      <Route path="/ref/:code" element={<RefLandingPage />} />
      <Route path="/reviews" element={<Reviews />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/profile/payment-methods" element={<PaymentMethods />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/customers/:userId/bookings" element={<CustomerBookings />} />
      <Route path="/checkout/:bookingId" element={<Checkout />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/auth/google" element={<AuthCallback />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/magic" element={<MagicLogin />} />
      <Route path="/pay/:token" element={<PayInvoiceByToken />} />
      <Route path="/tip/:bookingId" element={<Tip />} />
      <Route path="/claim/:token" element={<ClaimAccount />} />
      <Route path="/quote/:quoteId" element={<Quote />} />
      <Route path="/receipt/:txId" element={<Receipt />} />
      <Route path="/booking/:bookingId/confirm" element={<BookingConfirm />} />
      <Route path="/offer/:offerId" element={<Offer />} />
      {/* iter150 — Public policy pages for Twilio A2P 10DLC compliance.
          Both /privacy-policy and /privacy resolve; both
          /terms-and-conditions and /terms resolve. */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="*" element={<Home />} />
    </Routes>
  );

  // 1) Raw routes (login, payments, confirmations) — no chrome
  if (isRawRoute(location.pathname)) {
    return routesTable;
  }

  // 2) Admin in admin mode → AdminLayout chrome
  if (isAdmin && !overrideOn) {
    return <AdminLayout>{routesTable}<BackToRouteFab /></AdminLayout>;
  }

  // 3) Admin previewing customer site → customer Layout + return banner
  if (isAdmin && overrideOn) {
    return (
      <Layout>
        <CustomerPreviewBanner />
        {routesTable}
      </Layout>
    );
  }

  // 4) Customer / anonymous → standard customer chrome
  return <Layout>{routesTable}</Layout>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          {/* iter103 — Angi / Thumbtack referral trust splash. Mounted at
              the app root so it renders on ANY route where the customer
              lands from a marketplace. Auto-detects source via URL params
              or document.referrer and shows once per session. */}
          <ReferralWelcomeSplash />
          <Toaster position="top-center" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
